import { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import _cloneDeep from 'lodash/cloneDeep';
import _upperFirst from 'lodash/upperFirst';

import useFetch from './useFetch';
import useMutation from './useMutation';
import { AuthContext } from 'context/AuthContext';
import useSegment from './useSegment';
import isEmpty from 'lodash/isEmpty';

export default function useFeatures({
  loadOnMount = true,
  includeFeatures = [],
  excludeFeatures = [],
  defaultIsEnabled = false,
  isConfirmationRequired = true
} = {}) {
  const { updateState } = useContext(AuthContext);
  const { track } = useSegment();

  const [features, setFeatures] = useState(() => {
    if (!_isArray(includeFeatures)) return [];
    return includeFeatures.map(feature => ({
      feature_type: feature,
      is_enabled: defaultIsEnabled
    }));
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [featureData, setFeatureData] = useState(null);

  const featureMap = useMemo(() => {
    if (!_isArray(features) && !_isEmpty(features)) return {};
    return features.reduce((acc, feature) => {
      acc[feature.id] = feature;
      return acc;
    }, {});
  }, [features]);

  const featureId = _get(featureData, 'id');
  const featureTitle = _get(featureData, 'title');
  const isEnabled = _get(featureMap, `${featureId}.is_enabled`);
  const newEnabledState = isEnabled ? 'disable' : 'enable';

  const { isError, isLoading, fetchData } = useFetch(
    'configure.featureFlags.root',
    {
      loadOnMount: loadOnMount,
      payload: () => {
        const payload = {};
        const include = _isArray(includeFeatures) && includeFeatures.toString();
        const exclude = _isArray(excludeFeatures) && excludeFeatures.toString();
        if (include) payload.filter = include;
        if (exclude) payload.exclude = exclude;
        return payload;
      },
      onSuccess: features => {
        if (!_isArray(features)) return;

        if (_isArray(includeFeatures) && !isEmpty(includeFeatures)) {
          const featuresMap = features.reduce((acc, feature) => {
            const featureType = _get(feature, 'feature_type', '');
            acc[featureType] = feature;
            return acc;
          }, {});
          const sortedFeatures = includeFeatures.reduce((acc, type) => {
            const featureData = featuresMap[type];
            if (featureData) acc.push(featureData);
            return acc;
          }, []);
          return setFeatures(sortedFeatures);
        }
        setFeatures(features);
      }
    }
  );

  useEffect(() => {
    if (_isArray(features) && !_isEmpty(features)) {
      const exchangeFeature = features.find(({ feature_type }) => {
        return feature_type === 'is_exchange_allowed';
      });
      if (exchangeFeature) {
        const isExchangeAllowed = _get(exchangeFeature, 'is_enabled', true);
        updateState({ isExchangeAllowed });
      }
    }
  }, [features, updateState]);

  const { mutate, isProcessing } = useMutation(
    'configure.featureFlags.root',
    'PATCH',
    {
      routeParam: featureId,
      payload: { is_enabled: !isEnabled },
      errorMessage: `Unable to ${newEnabledState} the ${featureTitle} feature at the moment. Please try later!`,
      successMessage: `Successfully ${newEnabledState}d the ${featureTitle} feature.`,
      onSuccess: () => {
        onModalClose();
        track(`${_upperFirst(newEnabledState)}d Feature - ${featureTitle}`, {
          feature_id: featureId
        });
        setFeatures(currFeatures => {
          const index = currFeatures.findIndex(({ id }) => id === featureId);
          if (index === -1) return currFeatures;
          const features = _cloneDeep(currFeatures);
          _set(features, [index, 'is_enabled'], !isEnabled);
          return features;
        });
        setTimeout(() => onModalClosed(), 300);
      },
      onError: err => console.log(err)
    }
  );

  const onToggle = useCallback(
    (featureId = null) => {
      const feature = _get(featureMap, featureId, null);

      if (!feature) return;

      setFeatureData(feature);
      setIsModalOpen(isConfirmationRequired);
      if (!isConfirmationRequired) {
        mutate({
          routeParam: _get(feature, 'id', ''),
          is_enabled: !_get(feature, 'is_enabled', false)
        });
      }
    },
    [featureMap, isConfirmationRequired, mutate]
  );

  const onModalClose = useCallback(() => setIsModalOpen(false), []);

  const onModalClosed = useCallback(() => setFeatureData(null), []);

  return {
    features,
    featureMap,
    isError,
    isLoading,
    onReload: () => fetchData(),

    // Confirm Modal
    isModalOpen,
    modalData: featureData,
    isProcessing,
    processingId: _get(featureData, 'id', null),
    onToggle,
    onModalClose,
    onModalClosed,
    toggleFeature: () => mutate()
  };
}
